import { useEffect, useState } from "react";
import { Check, ChevronDown, Lock } from "lucide-react";
import { api } from "@/lib/api";

const STATE_CLS = {
  achieved: "border-sage bg-sage/15 text-charcoal",
  current: "border-terracotta bg-terracotta text-white",
  upcoming: "border-limestone bg-white text-warmgrey",
};

export const PartnerJourney = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/dealer/partner-program").then((r) => {
      setData(r.data);
      setOpen(r.data.status);
    }).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const pct = data.next_status
    ? Math.min(100, Math.round((100 * data.eligible_sqft) / Math.max(1, data.eligible_sqft + data.remaining_sqft_to_next)))
    : 100;
  const tier = data.journey.find((t) => t.code === open);

  return (
    <section className="mt-8 rounded-lg border border-terracotta/30 bg-white p-5" data-testid="partner-journey">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-warmgrey">Partner journey</p>
          <p className="mt-1 font-display text-2xl font-light text-charcoal" data-testid="partner-status-value">{data.status_label}</p>
          <p className="mt-1 text-xs text-muted-foreground" data-testid="partner-status-progress">
            {data.eligible_sqft.toLocaleString()} sq ft qualifying this {data.quarter}
            {data.next_status ? ` · ${data.remaining_sqft_to_next.toLocaleString()} sq ft to ${data.next_status.toLowerCase()}` : " · highest tier"}
          </p>
        </div>
        <p className="text-xs text-warmgrey" data-testid="partner-review-status">
          {data.review_status.review_at ? `Quarterly review: ${new Date(data.review_status.review_at).toLocaleDateString()}` : "Quarterly review by Zelligio"}
        </p>
      </div>

      <ol className="mt-4 grid grid-cols-4 gap-2" data-testid="partner-journey-tiers">
        {data.journey.map((t) => (
          <li key={t.code}>
            <button
              type="button"
              onClick={() => setOpen(t.code)}
              aria-pressed={open === t.code}
              data-testid={`partner-tier-${t.code.toLowerCase()}`}
              className={`flex w-full flex-col items-start rounded-md border px-3 py-2 text-left transition-colors duration-200 ${STATE_CLS[t.state]} ${open === t.code ? "ring-2 ring-terracotta/40" : ""}`}
            >
              <span className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide">
                {t.state === "achieved" && <Check className="h-3 w-3" />}
                {t.state === "upcoming" && <Lock className="h-3 w-3" />}
                {t.label.replace(" Partner", "")}
              </span>
              <span className="mt-0.5 text-[10px] opacity-80">{t.threshold_sqft ? `${t.threshold_sqft.toLocaleString()} sq ft / qtr` : "Entry tier"}</span>
            </button>
          </li>
        ))}
      </ol>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-limestone" data-testid="partner-status-bar">
        <div className="h-full rounded-full bg-terracotta transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      {tier && (
        <div className="mt-4 rounded-md bg-limestone/40 p-4" data-testid="partner-tier-detail">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-charcoal">{tier.label}</p>
            <span className="text-[10px] uppercase tracking-wide text-warmgrey">{tier.state}</span>
          </div>
          {tier.description && <p className="mt-1 text-xs text-muted-foreground">{tier.description}</p>}
          {tier.benefits.length === 0 ? (
            <p className="mt-2 text-xs text-warmgrey" data-testid="partner-tier-no-benefits">No published benefits for this tier yet.</p>
          ) : (
            <ul className="mt-2 space-y-1.5" data-testid="partner-tier-benefits">
              {tier.benefits.map((b) => (
                <li key={b.id} className="flex items-start gap-2 text-xs text-charcoal">
                  <ChevronDown className="mt-0.5 h-3 w-3 -rotate-90 text-terracotta" />
                  <span>
                    <span className="font-medium">{b.title}</span>
                    {b.description && <span className="text-muted-foreground"> — {b.description}</span>}
                    {b.mode === "manual" && <span className="ml-1 text-[10px] uppercase text-warmgrey">on request</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <p className="mt-3 text-[10px] leading-relaxed text-muted-foreground">{data.review_status.note}</p>
    </section>
  );
};
